/**
 * DifficultyManager - Manages difficulty selection and bot configuration.
 * Difficulty scales through bot count, role structure, zone restriction,
 * coordination, and positioning discipline - NOT through reaction time or stat buffs.
 */

import * as BABYLON from "babylonjs";

// Difficulty levels
export enum DifficultyLevel {
  EASY = "easy",       // 1 bot (ChaserBot), free roam, simple chase-ball-shoot
  MEDIUM = "medium",   // 2 bots (DefenderBot + StrikerBot) with zone restrictions
  HARD = "hard",       // 3 bots: Defender + Midfielder + Striker with coordination
  IMPOSSIBLE = "impossible" // 11 bots: Full team formation 4-3-3
}

// AI roles
export enum AIRole {
  // Existing roles (unchanged)
  CHASER = "chaser",       // Easy mode - simple ball chasing
  DEFENDER = "defender",   // Medium mode - stays in own half
  STRIKER = "striker",     // Medium mode - stays in opponent half
  // New roles for HARD mode
  MIDFIELDER = "midfielder", // Hard mode - links defense and attack
  // New roles for IMPOSSIBLE mode (4-3-3 formation)
  LEFT_BACK = "left_back",
  CENTER_BACK_LEFT = "center_back_left",
  CENTER_BACK_RIGHT = "center_back_right",
  RIGHT_BACK = "right_back",
  DEFENSIVE_MID = "defensive_mid",
  CENTRAL_MID = "central_mid",
  ATTACKING_MID = "attacking_mid",
  LEFT_WINGER = "left_winger",
  RIGHT_WINGER = "right_winger",
  CENTER_FORWARD = "center_forward"
}

// AI States - extends existing DefenderAI states
export enum AIState {
  IDLE = "idle",
  CHASE_BALL = "chase_ball",
  DRIBBLE = "dribble",
  SHOOT = "shoot",
  TACKLE = "tackle",
  RETURN_HOME = "return_home",
  // New states for Medium mode
  DEFEND = "defend",       // Defender: guard zone
  PASS = "pass",           // Defender: pass to striker
  WAIT = "wait",           // Striker: wait for pass
  RECEIVE = "receive",     // Striker: receive ball
  ATTACK = "attack",       // Striker: attack toward goal
  // New states for HARD/IMPOSSIBLE
  SUPPORT = "support",         // Midfielder: support play, link defense/attack
  ADVANCE = "advance",         // Midfielder: move forward with ball
  MARK = "mark",               // Defender: mark nearest attacker
  INTERCEPT = "intercept",     // Defender: intercept passes
  CLEAR = "clear",             // Defender: clear ball from danger
  PRESS = "press",             // Forward: apply light pressure
  HOLD_POSITION = "hold_position", // Formation: stay near anchor
  FORMATION_RETURN = "formation_return" // Return to formation anchor
}

// Zone restriction interface
export interface AllowedZone {
  minX: number;  // Minimum X boundary
  maxX: number;  // Maximum X boundary
  minZ: number;  // Minimum Z boundary (for half-field restriction)
  maxZ: number;  // Maximum Z boundary (for half-field restriction)
}

// Separation configuration interface
export interface SeparationConfig {
  minDistance: number;      // Minimum distance from teammates (default: 2.0)
  separationForce: number;  // Force strength for separation (default: 3.0)
}

// Bot configuration for spawning
export interface BotConfig {
  role: AIRole;
  homePosition: BABYLON.Vector3;
  allowedZone: AllowedZone;
  separationConfig: SeparationConfig;
}

// Default separation config
const DEFAULT_SEPARATION: SeparationConfig = {
  minDistance: 2.0,
  separationForce: 3.0
};

// Stadium bounds (from Ball.ts and Engine.ts)
const STADIUM_BOUND_X = 19.7;
const STADIUM_BOUND_Z = 29.7;
const GOAL_Z = 30;

/**
 * Static class managing difficulty configuration
 */
export class DifficultyManager {
  private static _currentDifficulty: DifficultyLevel = DifficultyLevel.EASY;

  /**
   * Set the current difficulty level
   */
  static setDifficulty(level: DifficultyLevel): void {
    this._currentDifficulty = level;
    console.log(`Difficulty set to: ${level}`);
  }

  /**
   * Get the current difficulty level
   */
  static getDifficulty(): DifficultyLevel {
    return this._currentDifficulty;
  }

  /**
   * Get bot configurations for the current difficulty
   * Returns array of {role, homePosition, allowedZone} for each bot
   */
  static getBotConfigs(): BotConfig[] {
    switch (this._currentDifficulty) {
      case DifficultyLevel.EASY:
        return this._getEasyConfigs();
      case DifficultyLevel.MEDIUM:
        return this._getMediumConfigs();
      case DifficultyLevel.HARD:
        return this._getHardConfigs();
      case DifficultyLevel.IMPOSSIBLE:
        return this._getImpossibleConfigs();
      default:
        return this._getEasyConfigs();
    }
  }

  /**
   * Easy mode: 1 ChaserBot with free roam
   */
  private static _getEasyConfigs(): BotConfig[] {
    return [
      {
        role: AIRole.CHASER,
        homePosition: new BABYLON.Vector3(0, 0, 12),
        allowedZone: {
          minX: -STADIUM_BOUND_X,
          maxX: STADIUM_BOUND_X,
          minZ: -STADIUM_BOUND_Z,
          maxZ: STADIUM_BOUND_Z
        },
        separationConfig: DEFAULT_SEPARATION
      }
    ];
  }

  /**
   * Medium mode: DefenderBot (own half) + StrikerBot (opponent half)
   */
  private static _getMediumConfigs(): BotConfig[] {
    return [
      {
        role: AIRole.DEFENDER,
        homePosition: new BABYLON.Vector3(0, 0, 15),
        // Defender stays in own half (z >= 0)
        allowedZone: {
          minX: -STADIUM_BOUND_X,
          maxX: STADIUM_BOUND_X,
          minZ: 0,           // Cannot cross into opponent half
          maxZ: STADIUM_BOUND_Z
        },
        separationConfig: DEFAULT_SEPARATION
      },
      {
        role: AIRole.STRIKER,
        homePosition: new BABYLON.Vector3(0, 0, -15),
        // Striker stays in opponent half (z <= 0)
        allowedZone: {
          minX: -STADIUM_BOUND_X,
          maxX: STADIUM_BOUND_X,
          minZ: -STADIUM_BOUND_Z,
          maxZ: 0            // Cannot cross into own half
        },
        separationConfig: DEFAULT_SEPARATION
      }
    ];
  }

  /**
   * Hard mode: Defender + Midfielder + Striker with coordination chain
   * Midfielder links defense and attack in central band
   */
  private static _getHardConfigs(): BotConfig[] {
    return [
      {
        role: AIRole.DEFENDER,
        homePosition: new BABYLON.Vector3(0, 0, 18),
        // Defender stays in own half (z >= 0)
        allowedZone: {
          minX: -STADIUM_BOUND_X,
          maxX: STADIUM_BOUND_X,
          minZ: 5,           // Stays deeper
          maxZ: STADIUM_BOUND_Z
        },
        separationConfig: DEFAULT_SEPARATION
      },
      {
        role: AIRole.MIDFIELDER,
        homePosition: new BABYLON.Vector3(0, 0, 10),
        // Midfielder moves in central band (z: -10 to 10)
        allowedZone: {
          minX: -STADIUM_BOUND_X,
          maxX: STADIUM_BOUND_X,
          minZ: -10,
          maxZ: 10
        },
        separationConfig: DEFAULT_SEPARATION
      },
      {
        role: AIRole.STRIKER,
        homePosition: new BABYLON.Vector3(0, 0, -18),
        // Striker stays in opponent half (z <= -5)
        allowedZone: {
          minX: -STADIUM_BOUND_X,
          maxX: STADIUM_BOUND_X,
          minZ: -STADIUM_BOUND_Z,
          maxZ: -5           // Cannot cross into midfielder zone
        },
        separationConfig: DEFAULT_SEPARATION
      }
    ];
  }

  /**
   * Impossible mode: 10 bots in 4-3-3 formation
   * Full team with formation-based positioning
   */
  private static _getImpossibleConfigs(): BotConfig[] {
    // 4-3-3 Formation positions (looking from opponent goal toward our goal)
    // Z axis: -30 is opponent goal, +30 is our goal
    const formationPositions: { role: AIRole; x: number; z: number }[] = [
      // Defenders (back 4)
      { role: AIRole.LEFT_BACK, x: -12, z: 18 },
      { role: AIRole.CENTER_BACK_LEFT, x: -5, z: 20 },
      { role: AIRole.CENTER_BACK_RIGHT, x: 5, z: 20 },
      { role: AIRole.RIGHT_BACK, x: 12, z: 18 },
      // Midfielders (middle 3)
      { role: AIRole.DEFENSIVE_MID, x: 0, z: 8 },
      { role: AIRole.CENTRAL_MID, x: -8, z: 5 },
      { role: AIRole.ATTACKING_MID, x: 8, z: 2 },
      // Forwards (front 3)
      { role: AIRole.LEFT_WINGER, x: -14, z: -12 },
      { role: AIRole.RIGHT_WINGER, x: 14, z: -12 },
      { role: AIRole.CENTER_FORWARD, x: 0, z: -18 }
    ];

    // Create configs with appropriate zone restrictions
    return formationPositions.map(pos => {
      // Determine zone based on role
      let zoneMinZ: number;
      let zoneMaxZ: number;
      let zoneMinX = -STADIUM_BOUND_X;
      let zoneMaxX = STADIUM_BOUND_X;

      switch (pos.role) {
        // Defenders - defensive third
        case AIRole.LEFT_BACK:
        case AIRole.CENTER_BACK_LEFT:
        case AIRole.CENTER_BACK_RIGHT:
        case AIRole.RIGHT_BACK:
          zoneMinZ = 5;
          zoneMaxZ = STADIUM_BOUND_Z;
          break;
        // Defensive mid - can drop into defense
        case AIRole.DEFENSIVE_MID:
          zoneMinZ = -5;
          zoneMaxZ = 20;
          break;
        // Central/Attacking mid - middle third
        case AIRole.CENTRAL_MID:
        case AIRole.ATTACKING_MID:
          zoneMinZ = -15;
          zoneMaxZ = 15;
          break;
        // Forwards - attacking third
        case AIRole.LEFT_WINGER:
        case AIRole.RIGHT_WINGER:
        case AIRole.CENTER_FORWARD:
          zoneMinZ = -STADIUM_BOUND_Z;
          zoneMaxZ = -5;
          break;
        default:
          zoneMinZ = -STADIUM_BOUND_Z;
          zoneMaxZ = STADIUM_BOUND_Z;
      }

      // Wider X restriction for wingers
      if (pos.role === AIRole.LEFT_WINGER) {
        zoneMinX = -STADIUM_BOUND_X;
        zoneMaxX = -3;
      } else if (pos.role === AIRole.RIGHT_WINGER) {
        zoneMinX = 3;
        zoneMaxX = STADIUM_BOUND_X;
      }

      return {
        role: pos.role,
        homePosition: new BABYLON.Vector3(pos.x, 0, pos.z),
        allowedZone: {
          minX: zoneMinX,
          maxX: zoneMaxX,
          minZ: zoneMinZ,
          maxZ: zoneMaxZ
        },
        separationConfig: DEFAULT_SEPARATION
      };
    });
  }

  /**
   * Get the number of bots for current difficulty
   */
  static getBotCount(): number {
    return this.getBotConfigs().length;
  }

  /**
   * Check if position is within allowed zone
   */
  static isPositionInZone(position: BABYLON.Vector3, zone: AllowedZone): boolean {
    return (
      position.x >= zone.minX &&
      position.x <= zone.maxX &&
      position.z >= zone.minZ &&
      position.z <= zone.maxZ
    );
  }

  /**
   * Clamp position to allowed zone with smooth boundary
   */
  static clampToZone(position: BABYLON.Vector3, zone: AllowedZone): BABYLON.Vector3 {
    return new BABYLON.Vector3(
      BABYLON.Scalar.Clamp(position.x, zone.minX, zone.maxX),
      position.y,
      BABYLON.Scalar.Clamp(position.z, zone.minZ, zone.maxZ)
    );
  }
}